import { IconButton } from '@civilgrid/ui/components';
import { useMutation } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import type { Table } from '@tanstack/react-table';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import { printGeoItem as apiPrintGeoItem } from '~/api/projects2';
import { getReasonForError } from '~/common/getReasonForError';
import { downloadCSV } from '~/common/miscUtils';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { GeoItem, OrganizationDetail, Project, TLoadedState } from '~/common/types';
import { Dropdown } from '~/components2/Dropdown';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import { Notify } from '~/components2/Notify';
import { SendEmailModal } from '~/components2/SendEmailModal';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '..';
import { csvEscapeField } from './csvEscapeField';
import { buildPotholeCsvExportFields } from './potholeCsvExportFields';

interface ExportDropdownProps {
  table: Table<GeoItem>;
  project: Project;
}

type TFormValues = {
  potholingReport: string;
  potholingPerformedBy: string;
  preparedBy: string;
};

interface PrintGeoItemVariables {
  projectId: string;
  geoItemIds: string[];
  exportType: 'KMZ' | 'potholeLog';
  reportInfo?: { [field: string]: any };
  ccEmails?: string[];
}

export function ExportDropdown({ table, project }: ExportDropdownProps) {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { currentOrganizationDetail, me } = useSnapshot(rootStore);
  const o = currentOrganizationDetail as TLoadedState & OrganizationDetail;
  const rows = table.getSelectedRowModel().rows;
  const [pdfOpen, setPdfOpen] = useState(false);
  const [kmzOpen, setKmzOpen] = useState(false);
  const pdfForm = useForm<TFormValues>();

  const mutation = useMutation({
    mutationKey: ['print-geo-item', { projectId, reportType: o.mobileReportType }],
    mutationFn: (opts: PrintGeoItemVariables) =>
      apiPrintGeoItem(opts.projectId, opts.geoItemIds, opts.exportType, opts.reportInfo, opts.ccEmails),
    onSuccess: (_data, variables) => {
      capturePosthogEvent(
        variables.exportType === 'KMZ' ? POSTHOG_EVENTS.potholingKmzDownloaded : POSTHOG_EVENTS.potholingPdfDownloaded,
        { project_id: project.id, organization_id: project.organization },
      );
      notifyExportQueued({
        message: 'Your report will be emailed to you in a few minutes.',
        ccEmails: variables.ccEmails,
      });
    },
    onError: (e, variables) => {
      capturePosthogEvent(
        variables.exportType === 'KMZ'
          ? POSTHOG_EVENTS.potholingKmzDownloadFailed
          : POSTHOG_EVENTS.potholingPdfDownloadFailed,
        { project_id: project.id, organization_id: project.organization, reason: getReasonForError(e) },
      );
      console.error('Error printing geo items: ', e);
      Notify.error({
        message: 'An error occurred while generating your report, please contact support.',
      });
    },
  });

  const sendKmzExport = (ccEmails: string[]) =>
    mutation.mutateAsync({
      projectId,
      geoItemIds: rows.map(({ id }) => id),
      exportType: 'KMZ',
      ...(ccEmails.length > 0 && { ccEmails }),
    });

  const openPdfExport = () => {
    pdfForm.reset({ potholingReport: '', potholingPerformedBy: me?.name ?? '', preparedBy: o?.name ?? '' });
    setPdfOpen(true);
  };

  const sendPdfExport = async (ccEmails: string[]) => {
    if (!(await pdfForm.trigger())) return false;
    await mutation.mutateAsync({
      projectId,
      geoItemIds: rows.map(({ id }) => id),
      exportType: 'potholeLog',
      reportInfo: pdfForm.getValues(),
      ...(ccEmails.length > 0 && { ccEmails }),
    });
  };

  const isDisabled = !rows.length;

  return (
    <>
      <Dropdown
        disabled={isDisabled}
        triggerElement={
          <IconButton icon="download" variant="primary" disabled={isDisabled} data-testid="potholing-export-button">
            Export
          </IconButton>
        }
        items={[
          {
            type: 'simple',
            label: 'Download Selected (CSV)',
            'data-testid': 'potholing-export-csv-item',
            onClick: () => {
              const csvRows: string[][] = [];

              // Potholes get their own export field list, independent of `potholeColumns`/
              // the on-screen table, so export order/grouping can differ from the display.
              if (o.mobileReportType === 'potholeLog') {
                const geoItems = rows.map((row) => row.original);
                const fields = buildPotholeCsvExportFields(geoItems);
                csvRows.push(fields.map((f) => f.header));
                geoItems.forEach((geoItem) => {
                  csvRows.push(fields.map((f) => f.getValue(geoItem)));
                });
              } else {
                const columnHeaders = table
                  .getAllColumns()
                  .filter((c) => c.id !== 'select')
                  .map((c) => c.columnDef.header) as string[];

                csvRows.push(columnHeaders);

                const columnIds = table
                  .getAllColumns()
                  .filter((c) => c.id !== 'select')
                  .map((c) => c.id ?? '');

                rows.forEach((row) => {
                  const r = columnIds.map((id) => row.getValue(id)?.toString() ?? '');
                  csvRows.push(r);
                });
              }

              let csvData = '';
              csvRows.forEach((row) => {
                csvData += `${row.map(csvEscapeField).join(',')}\n`;
              });

              downloadCSV(csvData, `${project.name}_${o.mobileReportType}.csv`);
              capturePosthogEvent(POSTHOG_EVENTS.potholingCsvDownloaded, {
                project_id: project.id,
                organization_id: project.organization,
                row_count: rows.length,
              });
            },
          },
          {
            type: 'simple',
            label: 'Download Selected (PDF)',
            'data-testid': 'potholing-export-pdf-item',
            onClick: openPdfExport,
          },
          {
            type: 'simple',
            label: 'Download Selected (KMZ)',
            'data-testid': 'potholing-export-kmz-item',
            onClick: () => setKmzOpen(true),
          },
        ]}
      />
      <SendEmailModal
        open={pdfOpen}
        onOpenChange={setPdfOpen}
        title="Enter Report Information"
        testIdPrefix="potholing-export-pdf"
        submitLabel="Submit"
        onSend={sendPdfExport}
      >
        <div className="flex flex-col gap-2">
          <ControlledInput
            control={pdfForm.control}
            name="potholingReport"
            label="Report Prepared For:"
            isRequired
            data-testid="potholing-export-pdf-report-for-input"
          />
          <ControlledInput
            control={pdfForm.control}
            name="potholingPerformedBy"
            label="Potholing Performed By:"
            isRequired
            data-testid="potholing-export-pdf-performed-by-input"
          />
          <ControlledInput
            control={pdfForm.control}
            name="preparedBy"
            label="Prepared By:"
            isRequired
            data-testid="potholing-export-pdf-prepared-by-input"
          />
        </div>
      </SendEmailModal>
      <SendEmailModal
        open={kmzOpen}
        onOpenChange={setKmzOpen}
        title="Export potholing data (KMZ)"
        testIdPrefix="potholing-export-kmz"
        onSend={sendKmzExport}
      />
    </>
  );
}
